import React, { useId, useMemo, useRef } from 'react';
import { classes } from '../utils';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

export interface Calculation {
  name: string;
  value: number;
  highlight?: boolean;
}

interface Modal {
  calculations: Calculation[];
  showIndicator: boolean;
}

export default function Modal({ calculations, showIndicator }: Modal) {
  const allCalculations = useMemo(() => {
    return [
      ...calculations,
      {
        name: 'Endpreis',
        value: Math.max(
          calculations.reduce((prev, curr) => prev + curr.value, 0),
          0
        ),
        highlight: true,
      } satisfies Calculation,
    ];
  }, [calculations]);

  const headRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const open = useRef(false);

  return (
    <div className="tw:sticky tw:inset-0 tw:flex tw:pointer-events-none">
      <div className="tw:flex-1 tw:flex tw:justify-end tw:items-end">
        <div
          onFocus={() => setTimeout(() => (open.current = true), 300)}
          onBlur={() => (open.current = false)}
          ref={headRef}
          tabIndex={0}
          className={classes(
            'tw:bg-white tw:text-black', // tw:rounded-md
            'tw:w-full tw:group',
            'tw:pointer-events-auto tw:isolate tw:overflow-hidden tw:select-none tw:md:max-w-[400px]'
          )}
          style={{
            boxShadow: '0 4px 20px 0 #0004',
          }}>
          <div
            className={classes(
              'tw:flex tw:flex-col',
              'tw:bg-black ' //tw:max-lg:p-0.5
            )}>
            <div
              id={id}
              onClick={(ev) => {
                const elem = document.getElementById(id);
                if (!elem || !open.current) return;
                if (
                  elem.contains(ev.nativeEvent.target as any) ||
                  ev.nativeEvent.target === elem
                ) {
                  headRef.current?.blur();
                }
              }}
              className="tw:flex tw:justify-between tw:p-4 tw:pr-8 tw:max-lg:bg-warning tw:bg-white tw:font-bold tw:max-lg:cursor-pointer tw:group/yellow">
              <div
                className={
                  'tw:max-lg:group-active/yellow:scale-90 tw:transition-transform tw:flex tw:items-center tw:gap-2'
                }>
                Deine Buchung
                <span
                  className={classes(
                    'tw:w-3 tw:h-3 tw:bg-primary tw:rounded-full tw:transition-all tw:lg:hidden tw:duration-300',
                    showIndicator
                      ? 'tw:opacity-60 tw:scale-100'
                      : 'tw:opacity-0 tw:scale-80'
                  )}></span>
              </div>

              <ChevronUpIcon
                className={classes(
                  'tw:size-6 tw:lg:hidden tw:transition-transform',
                  'tw:max-lg:group-focus:rotate-180'
                )}
              />
            </div>

            {allCalculations.map(({ name, value, highlight }, index) => (
              <div
                key={index}
                className="tw:grid tw:overflow-hidden tw:max-lg:grid-rows-0 tw:max-lg:group-focus:grid-rows-[1fr] tw:transition-[grid-template-rows]">
                <div className={'tw:min-h-0'}>
                  <div className="tw:pt-px">
                    <div
                      className={classes(
                        'tw:flex tw:justify-between tw:p-4 tw:font-bold',
                        highlight
                          ? 'tw:bg-primary tw:text-white'
                          : 'tw:bg-secondary-bg'
                      )}>
                      <span>{name}</span>
                      <span>
                        {value.toFixed(2).replace('.', ',').replace('-', '- ')}{' '}
                        €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
